import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Result, Tabs } from 'antd'
import { ClipboardList, IdCard, LayoutGrid, Monitor } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { usersRetrieveOptions } from 'src/api/main'
import { ContentSpinner, QueryErrorAlert } from 'src/components'
import { useDocumentTitle } from 'src/hooks'
import { useHasWorkspacePermissions, useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { WorkspacePermissionCode } from 'src/utils'
import { isWorkspaceOwnerFromSession } from 'src/utils/workspaceMenuAccess'

import {
  WorkspacePageShell,
  useWorkspaceRoute
} from '../../workspaces/pages/WorkspacePageShell'
import { UserAuditTab } from '../components/UserAuditTab'
import {
  type ConfirmAction,
  UserConfirmActionModal
} from '../components/UserConfirmActionModal'
import { UserDeleteModal } from '../components/UserDeleteModal'
import { UserDetailAccessTab } from '../components/UserDetailAccessTab'
import {
  UserDetailHero,
  type UserHeroAction
} from '../components/UserDetailHero'
import { UserDetailOverviewTab } from '../components/UserDetailOverviewTab'
import { UserEditDrawer } from '../components/UserEditDrawer'
import { UserImpersonateModal } from '../components/UserImpersonateModal'
import { UserResendInviteModal } from '../components/UserResendInviteModal'
import { UserSessionsTab } from '../components/UserSessionsTab'
import { UserSuspendModal } from '../components/UserSuspendModal'
import { useWorkspaceUser } from '../hooks'
import { composeDisplayName, deriveUserStatus } from '../utils'

type ModalAction = 'suspend' | 'reactivate' | 'delete' | 'resend'

type TabKey = 'overview' | 'access' | 'sessions' | 'audit'

// Hero actions handled by the generic confirm-and-mutate modal.
const CONFIRM_HERO_ACTIONS = new Set<UserHeroAction>([
  'resetPassword',
  'requireMfa',
  'disableMfa',
  'resetMfa',
  'revokeInvite'
])

const HERO_ACTION_TO_MODAL: Partial<Record<UserHeroAction, ModalAction>> = {
  suspend: 'suspend',
  reactivate: 'reactivate',
  delete: 'delete',
  resendInvite: 'resend'
}

export const WorkspaceUserDetailPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const { memberId } = useParams<{ memberId: string }>()
  const { user: session } = useSessionContext()
  const userId = Number(memberId)

  const valid = idValid && Number.isFinite(userId) && userId > 0
  // Viewing your own member page redirects to the account profile, which is the
  // canonical place to manage your own details. Skip the member fetches then.
  const isSelf = valid && session?.user?.id === userId

  // Impersonation ("log in as") is owner-only.
  const isOwner = isWorkspaceOwnerFromSession(session, workspaceId)
  const [
    canManageMembers,
    canInviteMembers,
    canAssignAppRoles,
    canViewAudit,
    canViewApps
  ] = useHasWorkspacePermissions([
    WorkspacePermissionCode.USERS_MANAGE,
    WorkspacePermissionCode.USERS_INVITE,
    WorkspacePermissionCode.APPLICATIONS_ACCESS,
    WorkspacePermissionCode.AUDIT_VIEW,
    WorkspacePermissionCode.APPLICATIONS_VIEW
  ])

  const { record, isLoading, isError, refetch } = useWorkspaceUser({
    workspaceId,
    userId,
    enabled: valid && !isSelf
  })
  const { data: profile } = useQuery({
    ...usersRetrieveOptions({ path: { id: userId } }),
    enabled: valid && !isSelf,
    refetchOnMount: 'always'
  })
  const displayName = record
    ? composeDisplayName(
        record.user.first_name,
        record.user.last_name,
        record.user.email
      )
    : undefined

  useDocumentTitle(displayName)

  const [tab, setTab] = useState<TabKey>('overview')
  const [editOpen, setEditOpen] = useState(false)
  const [modalAction, setModalAction] = useState<ModalAction | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [impersonateOpen, setImpersonateOpen] = useState(false)
  const closeAction = () => setModalAction(null)

  const displayRecord = useMemo(() => {
    if (!record || !profile) return record

    return {
      ...record,
      user: {
        ...record.user,
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        phone: profile.phone ?? null,
        position: profile.position ?? null,
        sso_id: profile.sso_id ?? null,
        language: profile.language ?? record.user.language,
        archived: profile.archived ?? record.user.archived,
        last_login: profile.last_login ?? record.user.last_login
      }
    }
  }, [profile, record])

  const status = useMemo(
    () => (displayRecord ? deriveUserStatus(displayRecord) : 'inactive'),
    [displayRecord]
  )

  const handleHeroAction = (action: UserHeroAction) => {
    if (action === 'edit') {
      setEditOpen(true)
      return
    }
    if (action === 'impersonate') {
      setImpersonateOpen(true)
      return
    }
    if (CONFIRM_HERO_ACTIONS.has(action)) {
      setConfirmAction(action as ConfirmAction)
      return
    }
    const modal = HERO_ACTION_TO_MODAL[action]
    if (modal) {
      setModalAction(modal)
    }
  }

  const tabItems = [
    {
      key: 'overview' as const,
      label: (
        <Flex align="center" gap={6}>
          <IdCard size={14} />
          <span>{t('users.detail.tabs.overview')}</span>
        </Flex>
      )
    },
    // The tab is the member's application access, which the backend both
    // serves and writes on `applications.access`.
    ...(canAssignAppRoles
      ? [
          {
            key: 'access' as const,
            label: (
              <Flex align="center" gap={6}>
                <LayoutGrid size={14} />
                <span>{t('users.detail.tabs.access')}</span>
              </Flex>
            )
          }
        ]
      : []),
    // Someone else's sessions are an administrative view, and their audit
    // trail is the workspace log filtered to them — each follows its own code.
    ...(canManageMembers
      ? [
          {
            key: 'sessions' as const,
            label: (
              <Flex align="center" gap={6}>
                <Monitor size={14} />
                <span>{t('users.detail.tabs.sessions')}</span>
              </Flex>
            )
          }
        ]
      : []),
    ...(canViewAudit
      ? [
          {
            key: 'audit' as const,
            label: (
              <Flex align="center" gap={6}>
                <ClipboardList size={14} />
                <span>{t('users.detail.tabs.audit')}</span>
              </Flex>
            )
          }
        ]
      : [])
  ]

  if (!valid) return null
  if (isSelf) return <Navigate to={paths.account.profile} replace />
  if (isLoading) {
    return (
      <WorkspacePageShell>
        <ContentSpinner minHeight={400} />
      </WorkspacePageShell>
    )
  }
  if (isError) {
    return (
      <WorkspacePageShell breadcrumbLeaf={t('users.breadcrumb.notFound')}>
        <QueryErrorAlert onRetry={() => void refetch()} />
      </WorkspacePageShell>
    )
  }
  if (!displayRecord) {
    return (
      <WorkspacePageShell breadcrumbLeaf={t('users.breadcrumb.notFound')}>
        <Result
          status="404"
          title={t('users.notFound.title')}
          subTitle={t('users.notFound.subtitle')}
          extra={
            <Button
              type="primary"
              onClick={() =>
                navigate(paths.workspaces.members(String(workspaceId)))
              }
            >
              {t('users.notFound.back')}
            </Button>
          }
        />
      </WorkspacePageShell>
    )
  }

  return (
    <WorkspacePageShell breadcrumbLeaf={displayName}>
      <Flex vertical gap={14}>
        <UserDetailHero
          record={displayRecord}
          status={status}
          onAction={handleHeroAction}
          canImpersonate={isOwner}
          canManage={canManageMembers}
          canInvite={canInviteMembers}
        />

        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as TabKey)}
          tabBarStyle={{ marginBottom: 0 }}
          items={tabItems}
        />

        {tab === 'overview' && (
          <UserDetailOverviewTab
            record={displayRecord}
            workspaceId={workspaceId}
            onEdit={() => setEditOpen(true)}
            onResetPassword={() => setConfirmAction('resetPassword')}
            onRequireMfa={() => setConfirmAction('requireMfa')}
            onDisableMfa={() => setConfirmAction('disableMfa')}
            onResetMfa={() => setConfirmAction('resetMfa')}
            onViewAudit={() => setTab('audit')}
            canManage={canManageMembers}
            canViewAudit={canViewAudit}
            canViewApps={canViewApps}
          />
        )}
        {tab === 'access' && canAssignAppRoles && (
          <UserDetailAccessTab
            workspaceId={workspaceId}
            userId={userId}
            organizationAccessId={displayRecord.id}
            currentRoles={displayRecord.roles}
            readOnly={!canAssignAppRoles}
          />
        )}
        {tab === 'sessions' && canManageMembers && (
          <UserSessionsTab organizationAccessId={displayRecord.id} />
        )}
        {tab === 'audit' && canViewAudit && (
          <UserAuditTab workspaceId={workspaceId} userId={userId} />
        )}

        <UserEditDrawer
          workspaceId={workspaceId}
          open={editOpen}
          user={displayRecord}
          onClose={() => setEditOpen(false)}
        />

        <UserSuspendModal
          workspaceId={workspaceId}
          open={modalAction === 'suspend' || modalAction === 'reactivate'}
          suspend={modalAction === 'suspend'}
          user={
            modalAction === 'suspend' || modalAction === 'reactivate'
              ? displayRecord
              : null
          }
          onClose={closeAction}
        />
        <UserDeleteModal
          workspaceId={workspaceId}
          open={modalAction === 'delete'}
          user={modalAction === 'delete' ? displayRecord : null}
          onClose={closeAction}
          onDeleted={() =>
            navigate(paths.workspaces.members(String(workspaceId)))
          }
        />
        <UserResendInviteModal
          workspaceId={workspaceId}
          open={modalAction === 'resend'}
          user={modalAction === 'resend' ? displayRecord : null}
          onClose={closeAction}
        />
        <UserConfirmActionModal
          workspaceId={workspaceId}
          open={confirmAction != null}
          action={confirmAction ?? 'revokeInvite'}
          user={confirmAction != null ? displayRecord : null}
          onClose={() => setConfirmAction(null)}
        />
        <UserImpersonateModal
          open={impersonateOpen}
          user={impersonateOpen ? displayRecord : null}
          onClose={() => setImpersonateOpen(false)}
        />
      </Flex>
    </WorkspacePageShell>
  )
}
